/**
 *  @file       origin-access-manager.js
 *              The Origin Access Manager is responsible for managing lists of origins with a given
 *              purpose and key. It contains methods to add, remove and get origins based on the `purpose`
 *              and `key`.
 * 
 *              To determine if an origin is allowed, at least one of these cases must be true:
 *              1. The origin is allowed for every key and any purpose
 *              2. The origin is allowed for every key and this purpose
 *              3. The origin is allowed for this key and any purpose
 *              4. The origin is allowed for this key and this purpose
 * 
 *  @author     Wes Garland <wes@distributive.newtwork>
 *  @author     Kirill Kirnichansky <kirill@distributive.newtwork>
 *  @author     Ryan Saweczko <ryansaweczko@distributive.network>
 */
'use strict';

const debugging = require('dcp/debugging').scope('OAM');

const newTemplate = {
  any: [],
  fetchArguments: [],
  fetchData: [],
  fetchWorkFunctions: [],
  sendResults: [],
};
const protocol = require('dcp/protocol');

/**
 * This class provides manages lists of origins for a given `purpose` and `key`.
 */
class OriginAccessManager
{
  /**
   * Construct an OriginAccessManager
   * @constructor
   */
  constructor (every, currentScheduler)
  {
    const cleanEvery = structuredClone(newTemplate);
    if (every)
    {
      for (let prop in every)
      {
        if (!(cleanEvery.hasOwnProperty(prop)))
          throw new Error(`invalid origin access type '${prop}'`);
      }
      for (let prop in cleanEvery)
      {
        if (every[prop])
        {
          if (!Array.isArray(every[prop]))
            throw new Error(`origin access type '${prop}' must be an Array`);
          cleanEvery[prop].push(...every[prop]);
        }
      }
    }
    
    this.origins = { every: cleanEvery };
    this.currentScheduler = currentScheduler || exports.currentScheduler;
  }

  /**
   * This method returns a list of origins for a given purpose and key.
   * Purpose must be a string.
   * A previously-added origin for the purpose null matches any purpose
   * A previously-added origin with the key null matches any key
   * 
   * @param {string|null} purpose
   * @param {string|null} key
   * 
   * @returns {string[]} - Array containing origin(s)
   */
  getAllowList(purpose, key=null)
  {
    if (typeof purpose !== 'string')
      throw new Error('purpose must be a string.');

    const uniqueURLs = new Set();
    
    for (const keyVal in this.origins)
      if (keyVal === 'every' || keyVal === key || key === null)
        for (const purposeVal in this.origins[keyVal])
          if (purposeVal === 'any' || purposeVal === purpose)
            this.origins[keyVal][purposeVal].forEach(url => uniqueURLs.add(url)); 

    const allowed = Array.from(uniqueURLs);
    const schedulerOrigin = protocol.getSchedulerConfigLocation()?.origin;
    for (let i = 0; i < allowed.length; i++)
      if (allowed[i] === this.currentScheduler)
        allowed[i] = schedulerOrigin;

    return allowed;
  }
  
  /** 
   * This method returns True when the origin is allowed for a given purpose and key.
   * 
   * @param {string} origin
   * @param {string} purpose
   * @param {string|null} [key=null]
   * 
   * @returns {boolean} - True if the origin is allowed, false if not.
   */ 
  isAllowed(origin, purpose, key = null)
  {
    if (typeof purpose !== 'string')
      throw new Error('purpose must be a string.');
    if (typeof key !== 'string' && key !== null)
      throw new Error('key must be a string or null.');

    const schedulerUrl = protocol.getSchedulerConfigLocation();

    // Notes: (1) Existing code used optional-chaining to dereference schedulerUrl.
    //            I'm keeping that pattern in a different way. (pfr)
    //        (2) Need to refactor the handling of fileURLs out of OAM because stricly speaking
    //            they have paths, not origins.  Make a FileAccessManager and when presented with a
    //            fileURL we'd use FileAccessManager instead of OriginAccessManager. I have a clean
    //            impl in mind. (pfr)
    function testSchedulerOrigin(url)
    {
      try
      {
        if (!Boolean(schedulerUrl))
          return false;
        if (typeof url === 'string')
          url = new URL(url);
        if (url.origin === schedulerUrl.origin)
          return true;
        if (url.protocol !== schedulerUrl.protocol)
          return false;
        return url.origin.endsWith('.' + schedulerUrl.hostname);
      }
      catch(e)
      {
        // This catch-handler is hit by localExec because it passes fileUrl.pathname to isAllowed.
        // And new URL(fileUrl.pathname) throws with TypeError: Invalid URL.
        // NOTE: We're using 'OAM' for both the debugging scope and subsystem because we get here
        //       with every localExec job, so we rarely want to turn the following on.
        debugging('OAM') && console.debug('OAM:isAllowed: Error in testSchedulerOrigin:', e);
        return false;
      }
    }

    if (origin === schedulerUrl?.origin || testSchedulerOrigin(origin))
      origin = this.currentScheduler;

    if (this.origins.every.any.includes(origin))
      return true;
    if (this.origins.every[purpose]?.includes(origin))
      return true;
    if (this.origins[key]?.any.includes(origin))
      return true;
    if (this.origins[key]?.[purpose]?.includes(origin))
      return true;
    return false;
  }
  
  /**
   * This method adds an origin with a specified purpose and key to the OriginAccessManager.
   * Purpose and key can be either strings or null.
   * 
   * @param {string} origin
   * @param {string|null} purpose
   * @param {string|null} key
   */
  add(origin, purpose, key)
  {
    if (typeof purpose !== 'string' && purpose !== null)
      throw new Error('purpose must be a string or null.');
    if (typeof key !== 'string' && key !== null)
      throw new Error('key must be a string or null.');

    if (purpose === null)
      purpose = 'any';
    if (key === null)
      key = 'every';

    // New keys create a clone of newTemplate to ensure the default purposes of
    // any, fetchArguments, fetchData, fetchWorkFunctions, and sendResults always exist
    if (!this.origins.hasOwnProperty(key))
      this.origins[key] = structuredClone(newTemplate);
    if (!this.origins[key].hasOwnProperty(purpose))
      this.origins[key][purpose] = [];

    if (this.origins[key][purpose].indexOf(origin) === -1)
      this.origins[key][purpose].push(origin);
    
  }
  
  /**
   * This method removes an origin with the given purpose and key.
   * null purpose and/or key will NOT match any purpose and key.
   * 
   * @param {string} origin
   * @param {string|null} purpose
   * @param {string|null} key
   * 
   * @returns {boolean} - false if origin, purpose and/or key doesn't exist.
   */
  remove(origin, purpose, key)
  {
    if (typeof purpose !== 'string' && purpose !== null)
      throw new Error('purpose must be a string or null.');
    if (typeof key !== 'string' && key !== null)
      throw new Error('key must be a string or null.');

    if (purpose === null)
      purpose = 'any';
    if (key === null)
      key = 'every';

    if (!this.origins[key])
      return false;
    if (!this.origins[key][purpose])
      return false;
  
    const idx = this.origins[key][purpose].indexOf(origin);
    if (idx === -1)
      return false;
    
    this.origins[key][purpose].splice(idx, 1);
    return true;
  }

  /**
   * This method removes all origins for all purposes for a given String key.
   * 
   * @param {string} key
   */
  remove_byKey(key)
  {
    if (typeof key !== 'string')
      throw new Error('key must be a string.');

    delete this.origins[key];
  }
}

exports.OriginAccessManager = OriginAccessManager;
exports.currentScheduler = Symbol('scheduler.location');
